"use client";

import { useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { QRCodeSVG } from "qrcode.react";
import getApp from "@/lib/firebase";
import { useTranslations } from "@/contexts/I18nContext";
import { buildInviteUrl } from "@/lib/inviteUrl";
import { userFacingMessage } from "@/lib/errors/userFacingError";

/**
 * Per-household invite QR code modal. Mirrors iOS `HouseholdQRCodeView.swift`
 * and Android `HouseholdQRCodeSheet.kt`.
 *
 * Renders a role picker (Adult / Child / Guest / Elderly Parent), calls the
 * `createInvite` Cloud Function with `{ householdId, intendedMemberType }`,
 * then displays a QR code encoding `https://app.zoeyorganizer.com/join?token=...`
 * along with a Share Invite button (Web Share API, with a clipboard fallback).
 *
 * The invite is reset whenever the role changes, matching iOS behaviour so the
 * shared link is never out of sync with the displayed role label.
 */
interface HouseholdInviteSheetProps {
  householdId: string;
  householdName: string;
  onClose: () => void;
}

type InviteRole = "adult" | "child" | "guest" | "elderly_parent";

const ROLES: InviteRole[] = ["adult", "child", "guest", "elderly_parent"];

interface CreateInviteResult {
  token?: string;
  expiresAt?: string;
}

export function HouseholdInviteSheet({
  householdId,
  householdName,
  onClose,
}: HouseholdInviteSheetProps) {
  const t = useTranslations();

  const [selectedRole, setSelectedRole] = useState<InviteRole>("adult");
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset invite state whenever role changes (matches iOS .onChange).
  useEffect(() => {
    setInviteToken(null);
    setErrorMessage(null);
    setCopied(false);
  }, [selectedRole]);

  const inviteUrl = useMemo(
    () => (inviteToken ? buildInviteUrl(inviteToken) : null),
    [inviteToken],
  );

  const roleLabel = (role: InviteRole): string =>
    t(`households.invite.role.${role}`);

  async function handleCreate() {
    setIsCreating(true);
    setErrorMessage(null);
    try {
      const functions = getFunctions(getApp());
      const createInvite = httpsCallable<
        { householdId: string; intendedMemberType: InviteRole },
        CreateInviteResult
      >(functions, "createInvite");
      const result = await createInvite({
        householdId,
        intendedMemberType: selectedRole,
      });
      const token = result.data?.token;
      if (token) {
        setInviteToken(token);
      } else {
        setErrorMessage(t("households.invite.error.failed"));
      }
    } catch (err) {
      setErrorMessage(userFacingMessage(err, t("households.invite.error.failed")));
    } finally {
      setIsCreating(false);
    }
  }

  async function handleShare() {
    if (!inviteUrl) return;
    const title = t("households.invite.share.title", { household: householdName });
    const text = t("households.invite.share.message", {
      role: roleLabel(selectedRole).toLowerCase(),
    });

    // Prefer the native Web Share API on mobile; fall back to clipboard.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text, url: inviteUrl });
        return;
      } catch {
        // User cancelled or share unavailable — fall through to clipboard.
      }
    }

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(inviteUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    } catch (err) {
      console.error("Failed to copy invite link:", err);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative z-50 w-full max-w-sm mx-4 mb-4 sm:mb-0 bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden text-gray-900 dark:text-white">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 dark:border-white/10 flex items-center justify-between">
          <h3 className="font-semibold">{t("households.invite.title")}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            {t("households.invite.done")}
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Household name */}
          <p className="text-center text-lg font-semibold">{householdName}</p>

          {/* Role picker */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              {t("households.invite.inviteAs")}
            </p>
            <div
              role="radiogroup"
              aria-label={t("households.invite.inviteAs")}
              className="grid grid-cols-2 gap-2"
            >
              {ROLES.map((role) => {
                const active = selectedRole === role;
                return (
                  <button
                    key={role}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSelectedRole(role)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      active
                        ? "text-white border-transparent"
                        : "border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5"
                    }`}
                    style={
                      active
                        ? { backgroundColor: "var(--accent-color, #00C7BE)" }
                        : undefined
                    }
                  >
                    {roleLabel(role)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* QR area — fixed-size box so the layout doesn't jump between states. */}
          <div className="flex items-center justify-center">
            <div className="w-60 h-60 flex items-center justify-center rounded-2xl bg-gray-50 dark:bg-white/5 p-4">
              {isCreating ? (
                <div className="flex flex-col items-center gap-2">
                  <div
                    className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
                    style={{
                      borderColor: "var(--accent-color, #00C7BE)",
                      borderTopColor: "transparent",
                    }}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("households.invite.generating")}
                  </p>
                </div>
              ) : inviteUrl ? (
                <QRCodeSVG
                  value={inviteUrl}
                  size={208}
                  level="M"
                  includeMargin={false}
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                    <path d="M14 14h3v3" />
                    <path d="M20 14v3" />
                    <path d="M14 20h3" />
                    <path d="M20 20v.01" />
                  </svg>
                  {errorMessage && (
                    <p className="text-xs text-red-500 text-center px-2">
                      {errorMessage}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Helper copy */}
          <div className="text-center space-y-1">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("households.invite.scanShare", {
                household: householdName,
                role: roleLabel(selectedRole),
              })}
            </p>
            <p className="text-xs text-gray-400">
              {t("households.invite.expires")}
            </p>
          </div>

          {/* Plain-text URL for desktop users (selectable). */}
          {inviteUrl && (
            <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-2">
              <p className="text-xs font-mono text-gray-600 dark:text-gray-300 break-all text-center">
                {inviteUrl}
              </p>
            </div>
          )}

          {/* Action button */}
          <div className="pt-2">
            {inviteUrl ? (
              <button
                type="button"
                onClick={handleShare}
                className="w-full py-2.5 rounded-lg text-white text-sm font-semibold"
                style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
              >
                {copied
                  ? t("households.invite.share.copied")
                  : t("households.invite.share.button")}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCreate}
                disabled={isCreating}
                className="w-full py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
              >
                {isCreating
                  ? t("households.invite.generating")
                  : t("households.invite.generate")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
